import { Badge } from '../badge';
import { Button } from '../button';
import { CheckIcon, StarIcon } from 'lucide-react';
import { useFieldContext } from './shared';
import { useFormReadOnly } from './form-read-only';
import { availableLanguages } from './available-languages';
import type { Language } from '@worldcoo/monolith/core';

export default function LanguagesPickerField(props: {
	defaultText?: string;
	setAsDefaultText?: string;
	defaultLanguage: string | undefined;
	onDefaultLanguageChange: (langCode: Language) => void;
	onLanguageAdded?: (langCode: Language) => void;
	onLanguageRemoved?: (langCode: Language) => void;
}) {
	const field = useFieldContext<Language[]>();
	const formReadOnly = useFormReadOnly();
	const isEditing = !formReadOnly;
	const selectedLangs = field.state.value ?? [];
	const defaultLang = props.defaultLanguage ?? 'SPA';

	const setDefaultLanguage = (value: Language) => {
		props.onDefaultLanguageChange(value);
	};

	return availableLanguages.map((lang) => {
		const isSelected = selectedLangs.includes(lang.code);
		const isDefault = defaultLang === lang.code;
		const isLastSelected = isSelected && selectedLangs.length === 1;
		return (
			<div
				key={lang.code}
				onClick={() => {
					if (!isEditing) return;
					const current = [...selectedLangs];
					const index = current.indexOf(lang.code);
					if (index < 0) {
						current.push(lang.code);
						if (current.length === 1) setDefaultLanguage(lang.code);
						props.onLanguageAdded?.(lang.code);
					} else {
						if (current.length === 1) return;
						current.splice(index, 1);
						if (defaultLang === lang.code) setDefaultLanguage(current[0]!);
						props.onLanguageRemoved?.(lang.code);
					}
					field.handleChange(current);
				}}
				className={`grid h-14 grid-cols-[1fr_auto_auto] items-center rounded-lg border px-4 transition-[color,border-color,opacity,filter] duration-200 ${
					!isEditing
						? isSelected
							? 'cursor-default border-transparent'
							: 'cursor-default border-transparent opacity-40 grayscale'
						: isLastSelected
							? 'border-primary cursor-default'
							: isSelected
								? 'border-primary cursor-pointer'
								: 'cursor-pointer border-[#e4e4e4] hover:border-[#c4c4c4]'
				}`}
			>
				<div className="flex items-center gap-2">
					<img src={lang.flagUrl} alt="" width={24} height={24} />
					<span className="text-sm font-medium">{lang.label}</span>
				</div>
				<div className="mr-3 flex items-center">
					{isDefault ? (
						<Badge className="gap-1">
							<StarIcon className="size-3 fill-current" />
							{props.defaultText ?? 'Default'}
						</Badge>
					) : isSelected && isEditing ? (
						<Button
							type="button"
							variant="ghost"
							className="font-normal"
							onClick={(e) => {
								e.stopPropagation();
								setDefaultLanguage(lang.code);
							}}
						>
							{props.setAsDefaultText ?? 'Set as default'}
						</Button>
					) : null}
				</div>
				<div
					className="grid transition-[grid-template-columns,opacity] duration-200"
					style={{ gridTemplateColumns: isEditing ? '1fr' : '0fr', opacity: isEditing ? 1 : 0 }}
				>
					<div className="-m-1 overflow-hidden p-1">
						<div
							className={`flex size-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors ${
								isSelected ? 'border-primary bg-primary text-primary-foreground' : 'border-input'
							} ${isLastSelected ? 'opacity-50' : ''}`}
						>
							{isSelected && <CheckIcon className="size-3.5" />}
						</div>
					</div>
				</div>
			</div>
		);
	});
}
